import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import PortalAdminLayout from "./PortalAdminLayout";
import "./PortalDashboard.css";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";

const BusinessManagement = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [businesses, setBusinesses] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [stripeFilter, setStripeFilter] = useState("");
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [suspendReason, setSuspendReason] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState({ type: "", text: "" });

  const checkPortalAdmin = useCallback(() => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      navigate("/login");
      return false;
    }

    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => {
            return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join(""),
      );

      const decoded = JSON.parse(jsonPayload);
      const hasPortalAdminAccess =
        decoded.role === "portal_admin" ||
        decoded.role === "R6" ||
        (decoded.roles &&
          (decoded.roles.includes("portal_admin") ||
            decoded.roles.includes("R6")));

      if (!hasPortalAdminAccess) {
        navigate("/portal-admin");
        return false;
      }
      return true;
    } catch (error) {
      navigate("/login");
      return false;
    }
  }, [navigate]);

  const fetchBusinesses = useCallback(async () => {
    const token = localStorage.getItem("ogestyle_token");
    setLoading(true);
    setError("");

    try {
      const response = await axios.get(`${API_BASE_URL}/api/admin/businesses`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setBusinesses(response.data.businesses || []);
    } catch (error) {
      setError("Failed to load businesses");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (checkPortalAdmin()) {
      fetchBusinesses();
    }
  }, [checkPortalAdmin, fetchBusinesses]);

  // Filter businesses based on search and filters
  const filteredBusinesses = businesses.filter((business) => {
    const matchesSearch =
      !search ||
      business.name?.toLowerCase().includes(search.toLowerCase()) ||
      business.ownerEmail?.toLowerCase().includes(search.toLowerCase()) ||
      business.ownerName?.toLowerCase().includes(search.toLowerCase());

    const matchesStatus =
      !statusFilter ||
      (statusFilter === "active" &&
        business.isActive &&
        !business.isSuspended) ||
      (statusFilter === "inactive" && !business.isActive) ||
      (statusFilter === "suspended" && business.isSuspended);

    const matchesStripe =
      !stripeFilter ||
      (stripeFilter === "connected" && business.stripeAccountId) ||
      (stripeFilter === "not_connected" && !business.stripeAccountId);

    return matchesSearch && matchesStatus && matchesStripe;
  });

  const handleSearch = (e) => {
    e.preventDefault();
    // Filtering is done client-side
  };

  const suspendBusiness = async (businessId) => {
    if (!suspendReason.trim()) {
      setMessage({
        type: "error",
        text: "Please provide a reason for suspension",
      });
      return;
    }

    const token = localStorage.getItem("ogestyle_token");
    try {
      await axios.post(
        `${API_BASE_URL}/api/admin/businesses/${businessId}/suspend`,
        { reason: suspendReason },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setMessage({ type: "success", text: "Business suspended successfully" });
      setShowSuspendModal(false);
      setSuspendReason("");
      fetchBusinesses();
      setTimeout(() => setMessage({ type: "", text: "" }), 3000);
    } catch (error) {
      setMessage({
        type: "error",
        text: error.response?.data?.message || "Failed to suspend business",
      });
    }
  };

  const unsuspendBusiness = async (businessId) => {
    if (!window.confirm("Reactivate this business? It will be visible to customers again and its professionals restored.")) {
      return;
    }

    const token = localStorage.getItem("ogestyle_token");
    try {
      await axios.post(
        `${API_BASE_URL}/api/admin/businesses/${businessId}/unsuspend`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setMessage({
        type: "success",
        text: "Business reactivated successfully",
      });
      fetchBusinesses();
      setTimeout(() => setMessage({ type: "", text: "" }), 3000);
    } catch (error) {
      setMessage({
        type: "error",
        text: error.response?.data?.message || "Failed to reactivate business",
      });
    }
  };

  const viewBusinessDetails = async (businessId) => {
    const token = localStorage.getItem("ogestyle_token");
    try {
      // Get business details along with visibility diagnostics
      const [businessRes, diagnosticsRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/admin/businesses`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios
          .get(
            `${API_BASE_URL}/api/admin/businesses/${businessId}/visibility-diagnostics`,
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          )
          .catch(() => ({ data: null })),
      ]);

      const business = businessRes.data.businesses?.find(
        (b) => b._id === businessId,
      );
      if (business) {
        setSelectedBusiness({
          ...business,
          diagnostics: diagnosticsRes.data,
        });
        setShowModal(true);
      }
    } catch (error) {
      setMessage({ type: "error", text: "Failed to load business details" });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Never";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Stats summary
  const stats = {
    total: businesses.length,
    active: businesses.filter((b) => b.isActive && !b.isSuspended).length,
    inactive: businesses.filter((b) => !b.isActive).length,
    suspended: businesses.filter((b) => b.isSuspended).length,
    stripeConnected: businesses.filter((b) => b.stripeAccountId).length,
  };

  return (
    <PortalAdminLayout>
      <div className="portal-page-container">
        {/* Page Header */}
        <div className="portal-page-header">
          <div className="portal-page-title">
            <h1>🏢 Business Management</h1>
            <p>View and manage all businesses on the platform</p>
          </div>
        </div>

        {/* Messages */}
        {message.text && (
          <div className={`portal-message ${message.type}`}>{message.text}</div>
        )}

        {error && <div className="portal-message error">{error}</div>}

        {/* Stats Cards */}
        <div className="portal-stats-grid" style={{ marginBottom: "24px" }}>
          <div className="portal-stat-card">
            <div className="stat-icon" style={{ background: "#dbeafe" }}>
              🏢
            </div>
            <div className="stat-content">
              <div className="stat-value">{stats.total}</div>
              <div className="stat-label">Total Businesses</div>
            </div>
          </div>
          <div className="portal-stat-card">
            <div className="stat-icon" style={{ background: "#d1fae5" }}>
              ✓
            </div>
            <div className="stat-content">
              <div className="stat-value">{stats.active}</div>
              <div className="stat-label">Active</div>
            </div>
          </div>
          <div className="portal-stat-card">
            <div className="stat-icon" style={{ background: "#fee2e2" }}>
              ✗
            </div>
            <div className="stat-content">
              <div className="stat-value">{stats.inactive}</div>
              <div className="stat-label">Inactive</div>
            </div>
          </div>
          <div className="portal-stat-card">
            <div className="stat-icon" style={{ background: "#fef3c7" }}>
              ⚠️
            </div>
            <div className="stat-content">
              <div className="stat-value">{stats.suspended}</div>
              <div className="stat-label">Suspended</div>
            </div>
          </div>
          <div className="portal-stat-card">
            <div className="stat-icon" style={{ background: "#ede9fe" }}>
              💳
            </div>
            <div className="stat-content">
              <div className="stat-value">{stats.stripeConnected}</div>
              <div className="stat-label">Stripe Connected</div>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="portal-card" style={{ marginBottom: "24px" }}>
          <form onSubmit={handleSearch} className="portal-filters-form">
            <div className="portal-filters-row">
              <div className="portal-filter-group" style={{ flex: 2 }}>
                <label>Search</label>
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="portal-input"
                />
              </div>
              <div className="portal-filter-group">
                <label>Status</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="portal-select"
                >
                  <option value="">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>
              <div className="portal-filter-group">
                <label>Stripe</label>
                <select
                  value={stripeFilter}
                  onChange={(e) => setStripeFilter(e.target.value)}
                  className="portal-select"
                >
                  <option value="">All</option>
                  <option value="connected">Connected</option>
                  <option value="not_connected">Not Connected</option>
                </select>
              </div>
            </div>
          </form>
        </div>

        {/* Results Count */}
        <p style={{ color: "#6b7280", fontSize: "14px", marginBottom: "16px" }}>
          Showing {filteredBusinesses.length} of {businesses.length} businesses
        </p>

        {/* Business List */}
        {loading ? (
          <div
            className="portal-card"
            style={{ padding: "60px", textAlign: "center" }}
          >
            <div className="loading-spinner"></div>
            <p style={{ color: "#6b7280", marginTop: "16px" }}>
              Loading businesses...
            </p>
          </div>
        ) : filteredBusinesses.length === 0 ? (
          <div
            className="portal-card"
            style={{ padding: "60px", textAlign: "center" }}
          >
            <p style={{ color: "#6b7280", fontSize: "16px" }}>
              No businesses found
            </p>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="portal-card portal-table-container desktop-only">
              <table className="portal-table">
                <thead>
                  <tr>
                    <th>Business</th>
                    <th>Owner</th>
                    <th>Locations</th>
                    <th>Stripe</th>
                    <th>Status</th>
                    <th style={{ textAlign: "center" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBusinesses.map((business) => (
                    <tr
                      key={business._id}
                      style={{
                        opacity:
                          business.isActive && !business.isSuspended ? 1 : 0.7,
                      }}
                    >
                      <td>
                        <div style={{ fontWeight: "600", marginBottom: "4px" }}>
                          {business.name || "Unnamed Business"}
                        </div>
                        <div style={{ fontSize: "12px", color: "#6b7280" }}>
                          Created: {formatDate(business.createdAt)}
                        </div>
                      </td>
                      <td>
                        <div style={{ marginBottom: "2px" }}>
                          {business.ownerName || "Unknown"}
                        </div>
                        <div style={{ fontSize: "12px", color: "#6b7280" }}>
                          {business.ownerEmail || "No email"}
                        </div>
                      </td>
                      <td>
                        <span
                          style={{
                            background: "#dbeafe",
                            color: "#1e40af",
                            padding: "4px 10px",
                            borderRadius: "9999px",
                            fontSize: "12px",
                            fontWeight: "500",
                          }}
                        >
                          {business.locationCount || 0} location
                          {business.locationCount !== 1 ? "s" : ""}
                        </span>
                      </td>
                      <td>
                        {business.stripeAccountId ? (
                          <span className="status-badge success">
                            ✓ Connected
                          </span>
                        ) : (
                          <span className="status-badge error">
                            ✗ Not Connected
                          </span>
                        )}
                      </td>
                      <td>
                        {business.isSuspended ? (
                          <span className="status-badge warning">
                            ⚠️ Suspended
                          </span>
                        ) : business.isActive ? (
                          <span className="status-badge success">Active</span>
                        ) : (
                          <span className="status-badge error">Inactive</span>
                        )}
                      </td>
                      <td>
                        <div
                          style={{
                            display: "flex",
                            gap: "8px",
                            justifyContent: "center",
                            flexWrap: "wrap",
                          }}
                        >
                          <button
                            onClick={() => viewBusinessDetails(business._id)}
                            className="portal-btn portal-btn-secondary portal-btn-sm"
                          >
                            View Details
                          </button>
                          {business.isSuspended ? (
                            <button
                              onClick={() => unsuspendBusiness(business._id)}
                              className="portal-btn portal-btn-success portal-btn-sm"
                              title="Reinstate the business: restores active status, its professionals and its locations."
                            >
                              Reactivate
                            </button>
                          ) : (
                            <button
                              onClick={() => {
                                setSelectedBusiness(business);
                                setShowSuspendModal(true);
                              }}
                              className="portal-btn portal-btn-warning portal-btn-sm"
                              title="Take this business off the platform: hidden from customers, no new bookings, Stripe stays connected, and the reason is recorded. Reversed with Reactivate."
                            >
                              Suspend
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="mobile-only">
              {filteredBusinesses.map((business) => (
                <div
                  key={business._id}
                  className="portal-card mobile-card"
                  style={{
                    marginBottom: "16px",
                    opacity:
                      business.isActive && !business.isSuspended ? 1 : 0.7,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      marginBottom: "12px",
                    }}
                  >
                    <div>
                      <h3
                        style={{
                          fontSize: "16px",
                          fontWeight: "600",
                          marginBottom: "4px",
                        }}
                      >
                        {business.name || "Unnamed Business"}
                      </h3>
                      <p style={{ fontSize: "12px", color: "#6b7280" }}>
                        {business.ownerEmail}
                      </p>
                    </div>
                    {business.isSuspended ? (
                      <span className="status-badge warning">Suspended</span>
                    ) : business.isActive ? (
                      <span className="status-badge success">Active</span>
                    ) : (
                      <span className="status-badge error">Inactive</span>
                    )}
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "12px",
                      marginBottom: "16px",
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontSize: "11px",
                          color: "#6b7280",
                          textTransform: "uppercase",
                          marginBottom: "4px",
                        }}
                      >
                        Owner
                      </div>
                      <div style={{ fontSize: "14px" }}>
                        {business.ownerName || "Unknown"}
                      </div>
                    </div>
                    <div>
                      <div
                        style={{
                          fontSize: "11px",
                          color: "#6b7280",
                          textTransform: "uppercase",
                          marginBottom: "4px",
                        }}
                      >
                        Locations
                      </div>
                      <div style={{ fontSize: "14px" }}>
                        {business.locationCount || 0}
                      </div>
                    </div>
                    <div>
                      <div
                        style={{
                          fontSize: "11px",
                          color: "#6b7280",
                          textTransform: "uppercase",
                          marginBottom: "4px",
                        }}
                      >
                        Stripe
                      </div>
                      {business.stripeAccountId ? (
                        <span
                          className="status-badge success"
                          style={{ fontSize: "11px" }}
                        >
                          Connected
                        </span>
                      ) : (
                        <span
                          className="status-badge error"
                          style={{ fontSize: "11px" }}
                        >
                          Not Connected
                        </span>
                      )}
                    </div>
                  </div>

                  <div
                    style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}
                  >
                    <button
                      onClick={() => viewBusinessDetails(business._id)}
                      className="portal-btn portal-btn-secondary portal-btn-sm"
                      style={{ flex: 1 }}
                    >
                      View Details
                    </button>
                    {business.isSuspended ? (
                      <button
                        onClick={() => unsuspendBusiness(business._id)}
                        className="portal-btn portal-btn-success portal-btn-sm"
                        style={{ flex: 1 }}
                      >
                        Reactivate
                      </button>
                    ) : (
                      <button
                        onClick={() => {
                          setSelectedBusiness(business);
                          setShowSuspendModal(true);
                        }}
                        className="portal-btn portal-btn-warning portal-btn-sm"
                        style={{ flex: 1 }}
                      >
                        Suspend
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Business Details Modal */}
        {showModal && selectedBusiness && (
          <div
            className="portal-modal-overlay"
            onClick={() => setShowModal(false)}
          >
            <div className="portal-modal" onClick={(e) => e.stopPropagation()}>
              <div className="portal-modal-header">
                <h2>Business Details</h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="portal-modal-close"
                >
                  ×
                </button>
              </div>
              <div className="portal-modal-body">
                <div className="portal-detail-grid">
                  <div className="portal-detail-item">
                    <label>Business Name</label>
                    <p>{selectedBusiness.name || "Unnamed"}</p>
                  </div>
                  <div className="portal-detail-item">
                    <label>Status</label>
                    {selectedBusiness.isSuspended ? (
                      <span className="status-badge warning">⚠️ Suspended</span>
                    ) : selectedBusiness.isActive ? (
                      <span className="status-badge success">Active</span>
                    ) : (
                      <span className="status-badge error">Inactive</span>
                    )}
                  </div>
                  <div className="portal-detail-item">
                    <label>Owner</label>
                    <p>{selectedBusiness.ownerName || "Unknown"}</p>
                  </div>
                  <div className="portal-detail-item">
                    <label>Owner Email</label>
                    <p>{selectedBusiness.ownerEmail || "No email"}</p>
                  </div>
                  <div className="portal-detail-item">
                    <label>Locations</label>
                    <p>{selectedBusiness.locationCount || 0} location(s)</p>
                  </div>
                  <div className="portal-detail-item">
                    <label>Stripe Status</label>
                    {selectedBusiness.stripeAccountId ? (
                      <span className="status-badge success">Connected</span>
                    ) : (
                      <span className="status-badge error">Not Connected</span>
                    )}
                  </div>
                  <div className="portal-detail-item">
                    <label>Business Types</label>
                    <div
                      style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}
                    >
                      {selectedBusiness.type?.length > 0 ? (
                        selectedBusiness.type.map((t, idx) => (
                          <span
                            key={idx}
                            style={{
                              background: "#ede9fe",
                              color: "#6b21a8",
                              padding: "4px 8px",
                              borderRadius: "4px",
                              fontSize: "12px",
                              fontWeight: "500",
                            }}
                          >
                            {t}
                          </span>
                        ))
                      ) : (
                        <span style={{ color: "#9ca3af" }}>None</span>
                      )}
                    </div>
                  </div>
                  <div className="portal-detail-item">
                    <label>Created</label>
                    <p>{formatDate(selectedBusiness.createdAt)}</p>
                  </div>
                </div>

                {selectedBusiness.stripeAccountId && (
                  <div
                    style={{
                      marginTop: "20px",
                      padding: "16px",
                      background: "#f8fafc",
                      borderRadius: "8px",
                    }}
                  >
                    <h4
                      style={{
                        marginBottom: "12px",
                        fontSize: "14px",
                        fontWeight: "600",
                      }}
                    >
                      Stripe Information
                    </h4>
                    <div style={{ fontSize: "13px", color: "#475569" }}>
                      <p>
                        <strong>Account ID:</strong>{" "}
                        {selectedBusiness.stripeAccountId}
                      </p>
                      {selectedBusiness.chargesEnabled !== undefined && (
                        <p style={{ marginTop: "8px" }}>
                          <strong>Charges:</strong>{" "}
                          {selectedBusiness.chargesEnabled ? (
                            <span style={{ color: "#059669" }}>Enabled</span>
                          ) : (
                            <span style={{ color: "#dc2626" }}>Disabled</span>
                          )}
                        </p>
                      )}
                      {selectedBusiness.payoutsEnabled !== undefined && (
                        <p style={{ marginTop: "8px" }}>
                          <strong>Payouts:</strong>{" "}
                          {selectedBusiness.payoutsEnabled ? (
                            <span style={{ color: "#059669" }}>Enabled</span>
                          ) : (
                            <span style={{ color: "#dc2626" }}>Disabled</span>
                          )}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {selectedBusiness.diagnostics && (
                  <div
                    style={{
                      marginTop: "20px",
                      padding: "16px",
                      background: "#fffbeb",
                      borderRadius: "8px",
                      border: "1px solid #fde68a",
                    }}
                  >
                    <h4
                      style={{
                        marginBottom: "12px",
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#92400e",
                      }}
                    >
                      Visibility Diagnostics
                    </h4>
                    <div style={{ fontSize: "13px", color: "#78350f" }}>
                      {selectedBusiness.diagnostics.summary?.businessVisible ? (
                        <p>✅ Business is visible to customers</p>
                      ) : (
                        <>
                          <p>❌ Business is NOT visible to customers</p>
                          {selectedBusiness.diagnostics.actionItems?.length >
                            0 && (
                            <div style={{ marginTop: "8px" }}>
                              <p
                                style={{
                                  fontWeight: "500",
                                  marginBottom: "4px",
                                }}
                              >
                                Issues to fix:
                              </p>
                              {selectedBusiness.diagnostics.actionItems.map(
                                (item, idx) => (
                                  <p
                                    key={idx}
                                    style={{
                                      marginTop: "4px",
                                      paddingLeft: "16px",
                                    }}
                                  >
                                    • {item.action}
                                  </p>
                                ),
                              )}
                            </div>
                          )}
                          {selectedBusiness.diagnostics.summary && (
                            <p style={{ marginTop: "8px", fontSize: "12px" }}>
                              Shops visible:{" "}
                              {
                                selectedBusiness.diagnostics.summary
                                  .shopsVisible
                              }{" "}
                              of{" "}
                              {selectedBusiness.diagnostics.summary.shopsTotal}
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}

                {selectedBusiness.isSuspended &&
                  selectedBusiness.suspendedReason && (
                    <div
                      style={{
                        marginTop: "20px",
                        padding: "16px",
                        background: "#fef2f2",
                        borderRadius: "8px",
                        border: "1px solid #fecaca",
                      }}
                    >
                      <h4
                        style={{
                          marginBottom: "8px",
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#991b1b",
                        }}
                      >
                        Suspension Reason
                      </h4>
                      <p style={{ fontSize: "13px", color: "#7f1d1d" }}>
                        {selectedBusiness.suspendedReason}
                      </p>
                      {selectedBusiness.suspendedAt && (
                        <p
                          style={{
                            fontSize: "12px",
                            color: "#b91c1c",
                            marginTop: "8px",
                          }}
                        >
                          Suspended on:{" "}
                          {formatDate(selectedBusiness.suspendedAt)}
                        </p>
                      )}
                    </div>
                  )}
              </div>
              <div className="portal-modal-footer">
                {!selectedBusiness.isSuspended && selectedBusiness.isActive && (
                  <button
                    onClick={() => {
                      setShowModal(false);
                      setShowSuspendModal(true);
                    }}
                    className="portal-btn portal-btn-warning"
                  >
                    Suspend Business
                  </button>
                )}
                {selectedBusiness.isSuspended && (
                  <button
                    onClick={() => {
                      unsuspendBusiness(selectedBusiness._id);
                      setShowModal(false);
                    }}
                    className="portal-btn portal-btn-success"
                  >
                    Reactivate Business
                  </button>
                )}
                <button
                  onClick={() => setShowModal(false)}
                  className="portal-btn portal-btn-secondary"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Suspend Modal */}
        {showSuspendModal && selectedBusiness && (
          <div
            className="portal-modal-overlay"
            onClick={() => setShowSuspendModal(false)}
          >
            <div
              className="portal-modal"
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: "500px" }}
            >
              <div className="portal-modal-header">
                <h2>Suspend Business</h2>
                <button
                  onClick={() => setShowSuspendModal(false)}
                  className="portal-modal-close"
                >
                  ×
                </button>
              </div>
              <div className="portal-modal-body">
                <p style={{ marginBottom: "16px", color: "#6b7280" }}>
                  Suspending <strong>{selectedBusiness.name}</strong> will hide
                  it from customers but keep Stripe connected.
                </p>
                <div className="portal-form-group">
                  <label>Reason for Suspension *</label>
                  <textarea
                    value={suspendReason}
                    onChange={(e) => setSuspendReason(e.target.value)}
                    placeholder="Enter the reason for suspension..."
                    className="portal-textarea"
                    rows={4}
                    style={{ width: "100%", resize: "vertical" }}
                  />
                </div>
              </div>
              <div className="portal-modal-footer">
                <button
                  onClick={() => suspendBusiness(selectedBusiness._id)}
                  className="portal-btn portal-btn-danger"
                  disabled={!suspendReason.trim()}
                >
                  Confirm Suspension
                </button>
                <button
                  onClick={() => {
                    setShowSuspendModal(false);
                    setSuspendReason("");
                  }}
                  className="portal-btn portal-btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </PortalAdminLayout>
  );
};

export default BusinessManagement;
